import {
  Component,
  DestroyRef,
  computed,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Subject, combineLatest, startWith, map, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { ExportSettingsComponent } from '../export-settings/export-settings.component';
import { ToolbarButton } from '../../../models/toolbar.models';
import { ExportSchedule } from 'src/app/core/models/core.models';
import { FormMode } from 'src/app/core/models/form.models';
import { AppState } from 'src/app/reducers';
import { deleteExportScheduleSuccess } from 'src/app/core/actions/object.actions';
import {
  ExportSchedulesScope,
  injectDeleteExportScheduleMutation,
  injectExportSchedulesQuery,
} from 'src/app/api/queries/export-schedule.queries';
import { LoggerService } from '@services/logger.service';
import { Actions, ofType } from '@ngrx/effects';
import { objectChanged } from 'src/app/core/actions/core.actions';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-schedules',
  templateUrl: './schedules.component.html',
  styleUrls: ['./schedules.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SchedulesComponent implements OnInit {
  // The child's `saved` output is what closes the form - the NgRx `success$` does
  // not fire for a schedule save.
  @ViewChild('exportSettings', { static: false })
  exportSettings?: ExportSettingsComponent;

  @Input() objectId!: string;
  @Input() objectClassId!: number;

  formMode$ = new Subject<FormMode | null>();
  schedule$ = new Subject<ExportSchedule | null>();

  // Set from the inputs on init, then from `objectChanged`.
  private readonly scope = signal<ExportSchedulesScope | null>(null);
  private readonly exportSchedulesQuery = injectExportSchedulesQuery(
    this.scope,
  );

  readonly schedules = computed(() => this.exportSchedulesQuery.data() ?? []);
  readonly loading = computed(() => this.exportSchedulesQuery.isLoading());

  protected logger = inject(LoggerService);
  private actions$ = inject(Actions);
  private readonly deleteExportScheduleMutation =
    injectDeleteExportScheduleMutation();
  private dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
  ) {}

  readonly showBackButton$ = this.formMode$.pipe(
    startWith(null),
    map((mode) => mode !== null),
  );

  readonly buttons$ = this.formMode$.pipe(
    startWith(null),
    map((mode) => {
      this.logger.debug('Form mode:', mode);
      const t = this.translate.instant.bind(this.translate);
      const all: ToolbarButton[] = [
        {
          type: 'button',
          variant: 'secondary',
          label: t('btn.cancel'),
          action: () => this.cancel(),
          shouldDisplay: mode !== null,
        },
        {
          type: 'button',
          variant: 'primary',
          label: t('btn.save'),
          action: () => this.save(),
          shouldDisplay: mode !== null,
        },
        {
          type: 'button',
          variant: 'primary',
          label: t('btn.add'),
          leadingIcon: ['fas', 'plus'],
          action: () => this.create(),
          shouldDisplay: mode === null,
        },
      ];
      return all.filter((b) => b.shouldDisplay);
    }),
  );

  readonly title$ = combineLatest([
    this.formMode$.pipe(startWith(null)),
    this.schedule$.pipe(startWith(null)),
  ]).pipe(
    map(([mode, schedule]) => {
      const t = this.translate.instant.bind(this.translate);
      if (mode === FormMode.Edit && schedule) {
        return t('export-schedule.edit.title', { name: schedule.title });
      }
      if (mode === FormMode.Create) {
        return t('export-schedule.create.title');
      }
      return t('export-schedule.title.schedules');
    }),
  );

  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);

  ngOnInit() {
    if (!this.objectId || this.objectClassId == null) {
      throw new Error('Both `objectId` and `objectClassId` are required');
    }

    this.scope.set({
      objectId: this.objectId,
      objectClassId: this.objectClassId,
    });

    this.actions$
      .pipe(
        ofType(objectChanged),
        tap((action) => {
          if (!action?.object) return;

          this.scope.set({
            objectId: action.object.id,
            objectClassId: action.object.object_class_id,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  create() {
    this.formMode$.next(FormMode.Create);
    this.schedule$.next(null);
  }

  scheduleEdit(s: ExportSchedule) {
    this.formMode$.next(FormMode.Edit);
    this.schedule$.next(s);
  }

  cancel() {
    this.formMode$.next(null);
    this.schedule$.next(null);
  }

  save() {
    this.exportSettings.submit();
  }

  trackById(_: number, item: ExportSchedule) {
    return item.id;
  }

  sidebarBack() {
    this.formMode$.next(null);
    this.schedule$.next(null);
  }

  delete(schedule: ExportSchedule) {
    const title = this.translateService.instant(
      'schedules.title.delete-operation',
    );

    const message = this.translateService.instant(
      'schedules.message.are-you-sure-you-want-to-delete-this-schedule?',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.deleteExportScheduleMutation
            .mutateAsync(schedule.id)
            .then(() =>
              this.store.dispatch(
                deleteExportScheduleSuccess({ exportScheduleId: schedule.id }),
              ),
            )
            .catch(() => undefined); // ! failures were silent before too
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }
}
